/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { custApi } from "../../lib/customerApi";
import { openRazorpay } from "../../lib/razorpay";
import { toast } from "sonner";
import { MePageTitle, MeSection, MeStagger } from "./MeUI";
import { MeServicePartners as NewServicePartners, MePartnerDetail as NewPartnerDetail } from "./MePartnerPages";
import {
  Leaf, Pill, Wheelchair, FirstAid, Stethoscope, TestTube, Truck, Warning,
  ArrowRight, MagnifyingGlass, MapPin, Clock, Phone, Storefront,
  ArrowLeft, House, Sparkle,
} from "@phosphor-icons/react";

// Icon map keyed by tile.icon string from backend
const ICONS = {
  Leaf, Pill, Wheelchair, FirstAid, Stethoscope, TestTube,
  Ambulance: Truck, Warning,
};

const CAT_COLOR = {
  retail: "#0B2B66",
  health: "#DC2626",
  delivery: "#C25D38",
};

// ====================================================================
//  Hub: /me/services  — six tiles
// ====================================================================
export function MeServicesHub() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    custApi.get("/me/services-overview")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load services"));
  }, []);

  if (err) return <Empty msg={err}/>;
  if (!data) return <Skeleton/>;

  return (
    <div className="font-body" data-testid="me-services">
      <MePageTitle
        eyebrow="Rescigo · Services"
        title={<>Everything you need,<br/><span className="text-[#DC2626]">all in one place</span></>}
        subtitle="Book healthcare, schedule waste pickup, order medicines and more."
      />

      <MeSection className="mt-2">
        <MeStagger stagger={35}>
          <section className="grid grid-cols-2 gap-3">
            {(data.tiles || []).map((t) => <ServiceTile key={t.key} tile={t}/>)}
          </section>
        </MeStagger>
      </MeSection>

      {/* Trust strip */}
      <MeSection className="mt-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-3 grid grid-cols-4 gap-2 text-center">
          {[
            { l: "CPCB",     s: "Compliant"   },
            { l: "Safe",     s: "Trained pros"},
            { l: "On-time",  s: "Punctual"    },
            { l: "Eco",      s: "Responsible" },
          ].map(item => (
            <div key={item.l}>
              <div className="text-[11px] font-bold text-[#DC2626]">{item.l}</div>
              <div className="text-[9px] text-gray-500 mt-0.5">{item.s}</div>
            </div>
          ))}
        </div>
      </MeSection>

      <MeSection className="mt-4 mb-6">
        <div className="text-[11px] text-gray-500 italic text-center">
          Don&apos;t see a service you need?{" "}
          <Link to="/contact" className="underline text-[#DC2626] font-semibold">Let us know</Link>{" "}
          — we&apos;re constantly adding partners.
        </div>
      </MeSection>
    </div>
  );
}

function ServiceTile({ tile }) {
  const Icon = ICONS[tile.icon] || Storefront;
  const count = tile.available_count;
  const isWaste = tile.key === "waste";
  const HEALTH_OVERRIDE = {
    doctor: "/me/health/doctors",
    lab: "/me/health/labs",
    ambulance: "/me/health/ambulance",
  };
  const to = isWaste
    ? "/me/requests?new=1"
    : (HEALTH_OVERRIDE[tile.subcategory] || `/me/services/${tile.subcategory}`);
  const unavailable = !isWaste && count === 0;
  return (
    <Link to={unavailable ? "#" : to}
          data-testid={`service-tile-${tile.key}`}
          onClick={(e) => unavailable && e.preventDefault()}
          className={`group bg-white border border-gray-100 rounded-2xl p-4 transition-transform active:scale-[0.98] flex flex-col gap-3 shadow-[0_8px_24px_rgba(0,0,0,0.04)] ${
            unavailable ? "opacity-60 cursor-not-allowed" : ""
          }`}>
      <div className="flex items-center justify-between">
        {isWaste ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-[#FEE2E2] text-[#DC2626] rounded-full px-2 py-1">
            <Sparkle size={9} weight="fill"/> Popular
          </span>
        ) : count > 0 ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-[#DCFCE7] text-[#15803D] rounded-full px-2 py-1">
            <Sparkle size={9} weight="fill"/> {count} nearby
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-gray-100 text-gray-500 rounded-full px-2 py-1">
            Coming soon
          </span>
        )}
      </div>
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FEE2E2] to-[#FFEDED] text-[#DC2626] flex items-center justify-center">
        <Icon size={28} weight="duotone"/>
      </div>
      <div>
        <div className="font-display font-bold text-[15px] text-[#111827] leading-tight">{tile.label}</div>
        <div className="text-[11px] text-gray-500 mt-1 leading-snug line-clamp-2">{tile.blurb}</div>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
          {isWaste ? "Book now" : count > 0 ? "Explore" : "Notify me"}
        </span>
        {!unavailable && <ArrowRight size={11} weight="bold" className="text-[#DC2626]"/>}
      </div>
    </Link>
  );
}

// ====================================================================
//  Subcategory list: /me/services/:subcategory
// ====================================================================
export const MeServicePartners = NewServicePartners;
export const MePartnerDetail = () => <NewPartnerDetail CheckoutModal={CheckoutModal} />;

// eslint-disable-next-line no-unused-vars
function OldServicePartners() {
  const { subcategory } = useParams();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [gps, setGps] = useState(null);
  const [tile, setTile] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams({ subcategory });
      if (q) params.set("q", q);
      if (gps) { params.set("near_lat", gps.lat); params.set("near_lng", gps.lng); }
      const r = await custApi.get(`/me/partners?${params}`);
      setList(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load partners"); }
  };
  useEffect(() => {
    custApi.get("/me/services-overview")
      .then(r => setTile((r.data.tiles || []).find(x => x.subcategory === subcategory)))
      .catch(() => {});
  }, [subcategory]);
  useEffect(() => { load(); }, [subcategory, gps]);

  const requestGps = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => toast.error(`Location denied: ${err.message}`),
    );
  };

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const TileIcon = ICONS[tile?.icon] || Storefront;
  const accent = CAT_COLOR[tile?.category] || "#DC2626";

  return (
    <div className="space-y-5" data-testid="me-service-partners">
      <Link to="/me/services" className="inline-flex items-center gap-1 text-xs text-[#5C5A55] hover:text-[#DC2626]">
        <ArrowLeft size={11}/> Back to all services
      </Link>

      <header className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg grid place-items-center text-white"
              style={{ background: accent }}>
          <TileIcon size={18} weight="duotone"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
            Rescigo · {tile ? capitalize(tile.category) : "Service"}
          </div>
          <h1 className="font-heading text-2xl text-[#1A1A1A]">
            {tile?.label || prettyLabel(subcategory)}
          </h1>
          {tile && <p className="text-xs text-[#5C5A55] mt-1">{tile.blurb}</p>}
        </div>
      </header>

      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={13} className="absolute left-3 top-3 text-[#8A8780]"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-search"
                  placeholder="Search by partner name or business"
                  className="w-full h-10 pl-9 pr-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#DC2626]"/>
        </div>
        <button type="submit"
                className="h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
          Search
        </button>
        <button type="button" onClick={requestGps} data-testid="use-location"
                className={`h-10 px-3 inline-flex items-center gap-1.5 rounded-md text-xs font-medium border ${
                  gps ? "border-[#DC2626] text-[#DC2626] bg-[#FEE2E2]"
                       : "border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2]"
                }`}>
          <MapPin size={11} weight={gps ? "fill" : "regular"}/>
          {gps ? "Sorting by nearest" : "Sort by nearest"}
        </button>
      </form>

      {!list ? <Skeleton/> : list.partners.length === 0 ? (
        <Empty msg="No partners listed in your area yet. Check back soon."/>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {list.partners.map((p) => (
            <PartnerCard key={p.id} p={p} accent={accent}/>
          ))}
        </div>
      )}
    </div>
  );
}

function PartnerCard({ p, accent }) {
  const today = new Date().toLocaleDateString("en-US", { weekday: "short" }).slice(0, 3).toLowerCase();
  const isOpenToday = (p.days_open || []).map(d => d.slice(0, 3)).includes(today);
  return (
    <Link to={`/me/services/partner/${p.id}`}
          data-testid={`partner-card-${p.id}`}
          className="bg-white border border-[#E5E2D9] rounded-xl p-4 hover:border-[#DC2626] hover:shadow-sm transition flex flex-col gap-2 group">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md grid place-items-center text-white flex-none"
              style={{ background: accent }}>
          <Storefront size={14} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base text-[#1A1A1A] truncate group-hover:text-[#DC2626]">{p.name}</div>
          {p.business_name && <div className="text-[11px] text-[#5C5A55] truncate">{p.business_name}</div>}
        </div>
        {p.distance_km != null && (
          <span className={`text-[10px] uppercase tracking-[0.15em] font-mono px-1.5 py-0.5 rounded ${
            p.in_radius ? "bg-[#FEE2E2] text-[#7F1D1D]" : "bg-[#F4C7B9] text-[#7A2E18]"
          }`}>
            {p.distance_km} km
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-y-1.5 text-[11px] text-[#5C5A55] mt-1">
        {p.address && (
          <div className="col-span-2 flex items-start gap-1.5">
            <MapPin size={10} className="mt-0.5 flex-none text-[#8A8780]"/>
            <span className="truncate">{p.address}</span>
          </div>
        )}
        {p.hours_open && (
          <div className="flex items-center gap-1.5">
            <Clock size={10} className="text-[#8A8780]"/>
            <span>{p.hours_open} – {p.hours_close}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Sparkle size={10} className={isOpenToday ? "text-[#DC2626]" : "text-[#8A8780]"}/>
          <span>{isOpenToday ? "Open today" : "Closed today"}</span>
        </div>
      </div>
      <div className="flex items-center justify-between mt-1 pt-2 border-t border-[#F0ECE3]">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
          {prettyLabel(p.service_mode)}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-[#DC2626] group-hover:translate-x-0.5 transition">
          View <ArrowRight size={11} weight="bold"/>
        </span>
      </div>
    </Link>
  );
}

// ====================================================================
//  Partner detail: /me/services/partner/:id  +  catalog + checkout
// ====================================================================
// eslint-disable-next-line no-unused-vars
function OldPartnerDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [products, setProducts] = useState([]);
  const [err, setErr] = useState("");
  const [checkout, setCheckout] = useState(null);  // product opened in modal

  useEffect(() => {
    custApi.get(`/me/partners/${id}`)
      .then(r => setP(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Could not load partner"));
    custApi.get(`/me/partners/${id}/products`)
      .then(r => setProducts(r.data.products || []))
      .catch(() => {});
  }, [id]);

  if (err) return <Empty msg={err}/>;
  if (!p) return <Skeleton/>;
  const accent = CAT_COLOR[p.category] || "#DC2626";

  return (
    <div className="space-y-5" data-testid="me-partner-detail">
      <Link to={`/me/services/${p.subcategory}`} className="inline-flex items-center gap-1 text-xs text-[#5C5A55] hover:text-[#DC2626]">
        <ArrowLeft size={11}/> Back to {prettyLabel(p.subcategory)} partners
      </Link>

      <header className="bg-white rounded-xl border border-[#E5E2D9] p-6 relative overflow-hidden">
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-5" style={{ background: accent }}/>
        <div className="relative flex items-start gap-4">
          <div className="w-14 h-14 rounded-lg grid place-items-center text-white" style={{ background: accent }}>
            <Storefront size={22} weight="duotone"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
              {capitalize(p.category)} · {prettyLabel(p.subcategory)}
            </div>
            <h1 className="font-heading text-3xl text-[#1A1A1A]">{p.name}</h1>
            {p.business_name && <p className="text-sm text-[#5C5A55]">{p.business_name}</p>}
          </div>
        </div>
        <dl className="mt-5 grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm relative">
          <Info icon={Phone} k="Phone" v={p.phone}/>
          <Info icon={MapPin} k="Address" v={p.address || "—"}/>
          <Info icon={Clock} k="Hours" v={p.hours_open ? `${p.hours_open} – ${p.hours_close}` : "Not specified"}/>
          <Info icon={House} k="Service mode" v={prettyLabel(p.service_mode)}/>
          <Info icon={MapPin} k="Coverage" v={`${p.service_radius_km} km radius`}/>
          <Info icon={Sparkle} k="Open days" v={(p.days_open || []).map(d => d[0].toUpperCase() + d.slice(1)).join(" · ") || "—"}/>
        </dl>
      </header>

      {/* Catalog */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Catalog</div>
            <h2 className="font-heading text-xl text-[#1A1A1A]">Available now</h2>
          </div>
          <Link to="/me/orders" className="text-xs text-[#DC2626] hover:underline inline-flex items-center gap-1">
            My orders <ArrowRight size={10} weight="bold"/>
          </Link>
        </div>
        {products.length === 0 ? (
          <div className="bg-white border border-[#E5E2D9] rounded-xl p-8 text-center text-sm text-[#8A8780]">
            {p.name} hasn't published listings yet. Check back soon.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="partner-catalog">
            {products.map(prod => (
              <ProductCard key={prod.id} prod={prod} accent={accent}
                            onOrder={() => setCheckout(prod)}/>
            ))}
          </div>
        )}
      </section>

      {checkout && (
        <CheckoutModal product={checkout} partner={p}
                        onClose={() => setCheckout(null)}/>
      )}
    </div>
  );
}

function ProductCard({ prod, accent, onOrder }) {
  const isRental = prod.type === "rental";
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-xl p-4 hover:border-[#DC2626] transition flex flex-col gap-2"
          data-testid={`product-card-${prod.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md grid place-items-center text-white flex-none" style={{ background: accent }}>
          <Storefront size={14} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base text-[#1A1A1A]">{prod.name}</div>
          {prod.description && <div className="text-[11px] text-[#5C5A55] line-clamp-2">{prod.description}</div>}
        </div>
        <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${
          isRental ? "bg-[#F4C7B9] text-[#7A2E18]" : "bg-[#FEE2E2] text-[#7F1D1D]"
        }`}>{prod.type}</span>
      </div>
      <div className="text-xs text-[#1A1A1A] font-mono">
        {isRental
          ? <>
              {prod.daily_rate != null && <span>₹{prod.daily_rate}/day · </span>}
              {prod.weekly_rate != null && <span>₹{prod.weekly_rate}/wk · </span>}
              {prod.monthly_rate != null && <span>₹{prod.monthly_rate}/mo</span>}
              {prod.deposit > 0 && <div className="text-[10px] text-[#8A8780] mt-0.5">Deposit ₹{prod.deposit}</div>}
            </>
          : <span>₹{(prod.price ?? 0).toFixed(2)} <span className="text-[10px] text-[#8A8780]">/ {prod.unit}</span></span>}
      </div>
      <div className="flex items-center justify-between mt-1 pt-2 border-t border-[#F0ECE3]">
        <span className="text-[10px] text-[#8A8780]">
          {prod.stock != null ? `${prod.stock} in stock` : "In stock"}
        </span>
        <button onClick={onOrder} data-testid={`order-${prod.id}`}
                 disabled={prod.stock === 0}
                 className="inline-flex items-center gap-1 text-xs h-8 px-3 rounded bg-[#DC2626] hover:bg-[#B91C1C] text-white disabled:opacity-50 disabled:cursor-not-allowed">
          {isRental ? "Rent now" : "Order"} <ArrowRight size={10} weight="bold"/>
        </button>
      </div>
    </div>
  );
}

function lineAmount(product, qty, days) {
  if (product.type !== "rental") return (product.price ?? 0) * qty;
  let rate = product.daily_rate || 0;
  if (days >= 30 && product.monthly_rate) rate = (Math.floor(days / 30) * product.monthly_rate + (days % 30) * (product.daily_rate || 0)) / days;
  else if (days >= 7 && product.weekly_rate) rate = (Math.floor(days / 7) * product.weekly_rate + (days % 7) * (product.daily_rate || 0)) / days;
  return rate * days * qty;
}

function CheckoutModal({ lines, partner, onClose, onQty }) {
  const [rentalDays, setRentalDays] = useState(1);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [payment, setPayment] = useState("online");  // online | cod
  const [busy, setBusy] = useState(false);
  const isRental = lines.some(l => l.product.type === "rental");
  const lineTotal = lines.reduce((s, l) => s + lineAmount(l.product, l.qty, rentalDays), 0);
  const depositTotal = lines.reduce((s, l) => s + (l.product.type === "rental" ? (l.product.deposit || 0) * l.qty : 0), 0);
  const grand = lineTotal + depositTotal;
  useEffect(() => { if (!lines.length) onClose(); }, [lines.length]);

  const submit = async () => {
    if (!address.trim()) { toast.error("Delivery address is required"); return; }
    setBusy(true);
    try {
      // Step 1: always create the order in our DB first (allows retry/cancel
      // if Razorpay flow falls through, and keeps inventory commit atomic)
      const r = await custApi.post("/me/orders", {
        partner_id: partner.id,
        items: lines.map(l => ({ product_id: l.product.id, qty: l.qty,
                   rental_days: l.product.type === "rental" ? rentalDays : null })),
        delivery_address: address.trim(),
        delivery_phone: phone.trim() || null,
        customer_notes: notes.trim() || null,
        payment_method: payment,
      });
      const placed = r.data;

      if (payment === "cod") {
        toast.success(`Order placed · ${placed.order_no}`);
        onClose();
        window.location.href = "/me/orders";
        return;
      }

      // Step 2: online — fetch a Razorpay checkout config + open SDK
      const chk = await custApi.post(`/me/orders/${placed.id}/checkout`);
      const cfg = chk.data;
      if (!cfg.key_id) {
        toast.error("Online payment not configured — order saved as COD. Switch from your order history if you change your mind.");
        onClose();
        window.location.href = "/me/orders";
        return;
      }
      let result;
      try {
        result = await openRazorpay(cfg);
      } catch (e) {
        toast(e.message || "Payment cancelled — your order is still saved as 'placed'. Pay later from your order history.",
              { icon: "ℹ️", duration: 6000 });
        onClose();
        window.location.href = "/me/orders";
        return;
      }
      // Step 3: signature verify on the server
      await custApi.post(`/me/orders/${placed.id}/verify-payment`, {
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });
      toast.success(`Payment successful · ${placed.order_no}`);
      onClose();
      window.location.href = "/me/orders";
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Could not place order"); }
    setBusy(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center p-3 overflow-auto"
          onClick={onClose} data-testid="checkout-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-md p-6 space-y-4"
            onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
              {isRental ? "Rent / order" : "Place order"}
            </div>
            <div className="font-heading text-xl">Your cart</div>
            <div className="text-[11px] text-[#5C5A55]">from {partner.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded" data-testid="ckt-close">×</button>
        </div>

        <div className="space-y-2" data-testid="ckt-lines">
          {lines.map(l => (
            <div key={l.product.id} className="flex items-center gap-2 text-sm" data-testid={`ckt-line-${l.product.id}`}>
              <div className="flex-1 min-w-0 truncate">{l.product.name}</div>
              <div className="flex items-center border border-[#E5E2D9] rounded-lg">
                <button type="button" onClick={() => onQty(l.product.id, l.qty - 1)} className="w-8 h-8" data-testid={`ckt-minus-${l.product.id}`}>−</button>
                <span className="w-6 text-center">{l.qty}</span>
                <button type="button" disabled={l.product.stock != null && l.qty >= l.product.stock} onClick={() => onQty(l.product.id, l.qty + 1)} className="w-8 h-8 disabled:opacity-30" data-testid={`ckt-plus-${l.product.id}`}>+</button>
              </div>
              <div className="w-20 text-right font-mono text-xs">₹{lineAmount(l.product, l.qty, rentalDays).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {isRental && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Rental days</div>
              <input type="number" min="1" value={rentalDays}
                      onChange={e => setRentalDays(Math.max(1, Number(e.target.value) || 1))}
                      data-testid="ckt-days"
                      className="w-full h-9 px-3 border border-[#E5E2D9] rounded text-sm"/>
            </label>
          )}
        </div>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Delivery address *</div>
          <textarea value={address} onChange={e => setAddress(e.target.value)} rows={2}
                    data-testid="ckt-address" required
                    placeholder="Door no, street, city, pincode"
                    className="w-full px-3 py-2 border border-[#E5E2D9] rounded text-sm"/>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Contact phone</div>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Optional"
                    className="w-full h-9 px-3 border border-[#E5E2D9] rounded text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Notes</div>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g., Call before delivery"
                    className="w-full h-9 px-3 border border-[#E5E2D9] rounded text-sm"/>
          </label>
        </div>

        {/* Payment method picker */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1.5">Payment method</div>
          <div className="grid grid-cols-2 gap-2" data-testid="ckt-payment-toggle">
            <PaymentOption on={payment === "online"} onClick={() => setPayment("online")}
                            title="UPI · Card · Netbanking" badge="Recommended"
                            sub="Pay now via Razorpay" testid="pay-online"/>
            <PaymentOption on={payment === "cod"} onClick={() => setPayment("cod")}
                            title="Cash on delivery"
                            sub={`Pay ${partner.name} on receipt`} testid="pay-cod"/>
          </div>
        </div>

        <div className="bg-[#F7F6F2] rounded p-3 text-xs space-y-1">
          <div className="flex justify-between">
            <span>{lines.reduce((n, l) => n + l.qty, 0)} item(s){isRental ? ` · ${rentalDays} rental day(s)` : ""}</span>
            <span className="font-mono">₹{lineTotal.toFixed(2)}</span>
          </div>
          {depositTotal > 0 && (
            <div className="flex justify-between text-[#5C5A55]">
              <span>Refundable deposit</span>
              <span className="font-mono">₹{depositTotal.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between pt-1 border-t border-[#E5E2D9] font-medium">
            <span>{payment === "online" ? "Pay now" : "Pay on delivery"}</span>
            <span className="font-mono text-base text-[#1A1A1A]">₹{grand.toFixed(2)}</span>
          </div>
        </div>

        <button onClick={submit} disabled={busy} data-testid="ckt-submit"
                 className="w-full h-11 rounded bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium disabled:opacity-60">
          {busy
            ? (payment === "online" ? "Launching checkout…" : "Placing order…")
            : (payment === "online" ? `Pay ₹${grand.toFixed(2)} securely` : "Place order")}
        </button>
        <div className="text-[10px] text-[#8A8780] text-center">
          {payment === "online"
            ? "Secured by Razorpay · UPI, cards, netbanking, wallets."
            : `You'll pay ${partner.name} directly on delivery.`}
        </div>
      </div>
    </div>,
    document.body
  );
}

function PaymentOption({ on, onClick, title, sub, badge, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
             className={`text-left p-3 rounded border transition relative ${
               on ? "border-[#DC2626] bg-[#FEE2E2]"
                  : "border-[#E5E2D9] bg-white hover:border-[#DC2626]"
             }`}>
      {badge && (
        <span className="absolute -top-2 right-2 px-1.5 py-0.5 text-[8px] uppercase tracking-[0.15em] rounded bg-[#DC2626] text-white">
          {badge}
        </span>
      )}
      <div className="text-xs font-medium text-[#1A1A1A] leading-tight">{title}</div>
      <div className="text-[10px] text-[#5C5A55] mt-0.5">{sub}</div>
    </button>
  );
}

// ====================================================================
//  Shared
// ====================================================================
const Skeleton = () => (
  <div className="space-y-3">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="h-20 bg-[#F0ECE3] rounded animate-pulse"/>
    ))}
  </div>
);
const Empty = ({ msg }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-xl p-8 text-center text-sm text-[#8A8780]">
    {msg}
  </div>
);
const Info = ({ icon: Icon, k, v }) => (
  <div className="flex items-start gap-2.5">
    <Icon size={13} className="mt-1 text-[#8A8780]"/>
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">{k}</div>
      <div className="text-[#1A1A1A]">{v || "—"}</div>
    </div>
  </div>
);

function prettyLabel(s) {
  if (!s) return "";
  return s.split("_").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
}
function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : "";
}
